import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setSection, updateSection } from "../../../assignmentSlice";
import { getStartingIndex } from "../../../../../utils/resumeUtils";
import { parseScrambleQuestion } from "../../../../../utils/parseUtils";

import ScrambleButton from "../../widgets/ScrambleButton";
import CommonPage from "../../common/CommonPage";

// 보기 조각은 "the environment" 처럼 낱말 여러 개일 수 있다.
// 정답 문장을 공백으로 쪼개면 조각 수와 어긋나서, 다 골라도 "모든 단어를 선택해주세요"가 뜬다.
// 그래서 정답 문장을 조각 단위로 되짚어 순서를 만든다.
const normalizeSpace = (text) => String(text ?? "").replace(/\s+/g, " ").trim();

// 맞춰 보기용 형태. **문장부호를 떼고 견준다.** 정답에는 마침표가 붙어 있는데
// 조각에는 없어서(조각 "attractive" vs 정답 "attractive.") 맞추기가 끝까지 못
// 가고 예전 방식으로 떨어졌다. 아이는 조각을 다 골라도 계속 막혔다.
// 아포스트로피는 남긴다. don't 가 don t 로 갈라지면 안 된다.
const comparisonKey = (text) =>
  normalizeSpace(
    String(text ?? "")
      .toLowerCase()
      .replace(/[\u2018\u2019\u02BC]/g, "'")
      .replace(/[^0-9a-z\u00C0-\u024F'\s]/g, " ")
  );

const buildChunkAnswer = (answerText, chunkList) => {
  if (!Array.isArray(chunkList) || chunkList.length === 0) return null;

  const pool = chunkList
    .map((chunk) => ({ ...chunk, key: comparisonKey(chunk.text), used: false }))
    .filter((chunk) => chunk.key.length > 0);
  let rest = comparisonKey(answerText);
  const ordered = [];

  while (rest.length > 0) {
    // 긴 조각부터 맞춰 본다. "of" 가 "of the" 를 가로채지 않도록.
    const candidate = pool
      .filter((chunk) => !chunk.used)
      .sort((a, b) => b.key.length - a.key.length)
      .find((chunk) => rest === chunk.key || rest.startsWith(chunk.key + " "));

    if (!candidate) return null; // 못 맞추면 예전 방식으로 돌아간다
    candidate.used = true;
    ordered.push({ text: candidate.text, index: candidate.index });
    rest = normalizeSpace(rest.slice(candidate.key.length));
  }

  return ordered.length > 0 ? ordered : null;
};

const ScramblePage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [wordList, setWordList] = useState([]);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const currentPage = pageList[currentIndex] || {
    question: "",
    hint: "",
    answer: "",
  };

  useEffect(() => {
    if (currentPage.parsedQuestion) {
      const scrambleElement = currentPage.parsedQuestion.find(
        (element) => element.type === "scramble"
      );
      if (scrambleElement) {
        setWordList(scrambleElement.content);
      }
    }
  }, [currentIndex, currentPage]);

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }
    setPageList(
      section.question.data.map((question) => {
        const parsedQuestion = parseScrambleQuestion(question.question);

        // Extract clean answer (remove context text)
        let cleanAnswer = question.answer;
        let scrambleContent;

        for (let i = 0; i < parsedQuestion.length; i++) {
          if (parsedQuestion[i].type === "text") {
            cleanAnswer = cleanAnswer.replace(parsedQuestion[i].content.trim(), "");
          } else if (parsedQuestion[i].type === "scramble") {
            scrambleContent = parsedQuestion[i].content;
          }
        }

        // Structure the answer as word objects
        cleanAnswer =
          buildChunkAnswer(cleanAnswer, scrambleContent) ||
          cleanAnswer.trim().split(/\s+/).map((word) => {
            return {
              text: word,
              index: scrambleContent.find((content) => content.text === word)?.index || 0
            };
          });

        return {
          ...question,
          parsedQuestion: parsedQuestion,
          cleanAnswer: cleanAnswer,
          input: [],
          isSubmitted: false,
          isCorrect: false,
        };
      })
    );
    setCurrentIndex(0);
  }, [section]);

  const handleClickWord = (word) => {
    if (currentPage.isSubmitted) return;

    const prevInput = currentPage.input || [];
    const existingIndex = prevInput.findIndex((item) => item.index === word.index);

    let currentInput;
    if (existingIndex >= 0) {
      currentInput = prevInput.filter((item) => item.index !== word.index);
    } else {
      currentInput = [...prevInput, word];
    }

    const newPageList = pageList.map((prevPage, index) => {
      if (index === currentIndex) {
        return {
          ...prevPage,
          input: currentInput,
        };
      }
      return prevPage;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const handleNextPage = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllAnswered = true;

      pageList.forEach((page) => {
        if (!page.isSubmitted) {
          isAllAnswered = false;
        }
      });

      if (isAllAnswered) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습이 완료되었습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) return;

    const input = currentPage.input || [];

    // 화면에 있는 조각보다 많이 요구하지 않는다. 조각 수와 정답 낱말 수가 어긋나면
    // 다 골라도 영영 제출하지 못한다(2026-08 박세빈 학원). 조각을 다 쓴 아이는 보낸다.
    const requiredCount = Math.min(currentPage.cleanAnswer.length, wordList.length);

    if (input.length < requiredCount) {
      alert("모든 단어를 선택해주세요.");
      return;
    }

    // 채점은 이어 붙인 문장으로 견준다. 조각을 어떻게 끊었든("the result" 한 덩어리
    // 인지 두 낱말인지) 같은 문장이면 맞은 것이고, 마침표와 대소문자는 보지 않는다.
    const inputKey = comparisonKey(input.map((word) => word.text).join(" "));
    const answerKey = comparisonKey(
      currentPage.cleanAnswer.map((word) => word.text).join(" ")
    );
    const correct = inputKey.length > 0 && inputKey === answerKey;

    const newPageList = pageList.map((page, index) => {
      if (index === currentIndex) {
        return {
          ...page,
          isSubmitted: true,
          isCorrect: correct,
        };
      }
      return page;
    });

    setOverlayVisible(true);
    setIsCorrect(correct);
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));

    setTimeout(() => {
      setOverlayVisible(false);
    }, 600);
  };

  return (
    <CommonPage
      currentIndex={currentIndex}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      currentPage={currentPage}
      totalNo={pageList.length}
      overlayVisible={overlayVisible}
      isCorrect={isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "40px",
          width: "90%",
        }}
      >
        <div style={{ fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "14px" : "20px", fontWeight: 700, textAlign: "center" }}>주어진 단어를 올바른 순서로 배열하세요.</div>
        <div
          style={{
            fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "14px" : "18px", // [문장테스트 통일]
            textAlign: "center",
            lineHeight: "1.5",
            color: "#666",
          }}
        >
          {currentPage.hint}
        </div>

        {/* Display sentence with blanks for scrambled section */}
        <div
          style={{
            minHeight: "60px",
            padding: "15px 20px",
            fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "17px" : "24px", // [문장테스트 통일]
            borderBottom: "1px solid #aaa",
            width: "80%",
            textAlign: "center",
            lineHeight: "1.5",
          }}
        >
          {currentPage.parsedQuestion && (
            <div style={{ wordBreak: "break-word", display: "inline" }}>
              {currentPage.parsedQuestion
                .map((element) => {
                  if (element.type === "scramble") {
                    const blankLength = element.length;
                    const userAnswer = currentPage.input || [];

                    return userAnswer
                      .map((answer) => answer.text)
                      .join(" ")
                      .concat(" ")
                      .padEnd(blankLength, "_");
                  } else {
                    return element.content;
                  }
                })
                .join(" ")}
            </div>
          )}
        </div>

        {/* Scrambled word buttons */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            justifyContent: "center",
            maxWidth: "700px",
          }}
        >
          {wordList.length > 0 && (
            <ScrambleButton
              wordList={wordList}
              currentPage={currentPage}
              handleClickWord={handleClickWord}
              type={"sentence"}
            />
          )}
        </div>

        {/* Show correct answer after submission if incorrect */}
        {currentPage.isSubmitted && !currentPage.isCorrect && (
          <div
            style={{
              fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "14px" : "18px", // [문장테스트 통일]
              color: "#d32f2f",
              textAlign: "center",
            }}
          >
            정답: {currentPage.answer}
          </div>
        )}
      </div>
    </CommonPage>
  );
};

export default ScramblePage;
